import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import {
  normalizeSavedSearchUrlInput,
  shouldSkipPersistingSavedSearchUrl,
} from '../../utils/saved-searches/normalize-saved-search-url';

export interface SearchEntry {
  url: string;
  timestamp: number;
}

interface SavedSearchesState {
  /** Single list: both auto-added (on search) and manually saved searches, most recent first. */
  savedSearches: SearchEntry[];
}

const initialState: SavedSearchesState = {
  savedSearches: [],
};

const MAX_SAVED_SEARCHES = 50;

function addSearchToState(list: SearchEntry[], url: string, maxSize: number): SearchEntry[] {
  const filtered = list.filter((s) => s.url !== url);
  filtered.unshift({ url, timestamp: Date.now() });
  return filtered.slice(0, maxSize);
}

function normalizeSearchEntryList(entries: SearchEntry[]): SearchEntry[] {
  const byUrl = new Map<string, SearchEntry>();
  for (const entry of entries) {
    if (entry?.url == null || typeof entry.url !== 'string') continue;
    const norm = normalizeSavedSearchUrlInput(entry.url);
    if (!norm) continue;
    if (shouldSkipPersistingSavedSearchUrl(norm)) continue;
    const prev = byUrl.get(norm);
    const next: SearchEntry = { url: norm, timestamp: entry.timestamp };
    if (!prev || next.timestamp > prev.timestamp) {
      byUrl.set(norm, next);
    }
  }
  return Array.from(byUrl.values())
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, MAX_SAVED_SEARCHES);
}

const savedSearchesSlice = createSlice({
  name: 'recentSearches',
  initialState,
  reducers: {
    /** Add a search (by URL). Used both when user runs a search and when they click "Save search". */
    addSearch: (state, action: PayloadAction<string>) => {
      if (!state.savedSearches) state.savedSearches = [];
      const url = normalizeSavedSearchUrlInput(action.payload);
      if (shouldSkipPersistingSavedSearchUrl(url)) return;
      state.savedSearches = addSearchToState(state.savedSearches, url, MAX_SAVED_SEARCHES);
    },
    removeSearch: (state, action: PayloadAction<string>) => {
      if (!state.savedSearches) state.savedSearches = [];
      const needle = normalizeSavedSearchUrlInput(action.payload);
      state.savedSearches = state.savedSearches.filter(
        (s) => normalizeSavedSearchUrlInput(s.url) !== needle,
      );
    },
    setSavedSearches: (state, action: PayloadAction<SearchEntry[]>) => {
      state.savedSearches = Array.isArray(action.payload) ? action.payload : [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: any) => {
      const persisted = action.payload?.recentSearches;
      if (!persisted) {
        if (!state.savedSearches) state.savedSearches = [];
        return state;
      }
      // Migrate from old two-list format: merge saved + recent, dedupe by URL, keep order by timestamp
      const saved = Array.isArray(persisted.savedSearches) ? persisted.savedSearches : [];
      const recent = Array.isArray(persisted.recentSearches) ? persisted.recentSearches : [];
      const merged: SearchEntry[] = [];
      const byUrl = new Map<string, SearchEntry>();
      [...saved, ...recent].forEach((entry) => {
        const existing = byUrl.get(entry.url);
        if (!existing || entry.timestamp > existing.timestamp) byUrl.set(entry.url, entry);
      });
      merged.push(...Array.from(byUrl.values()));
      state.savedSearches = normalizeSearchEntryList(merged);
      return state;
    });
  },
});

export const { addSearch, removeSearch, setSavedSearches } = savedSearchesSlice.actions;

/** @deprecated Use addSearch. Kept for compatibility. */
export const addRecentSearch = savedSearchesSlice.actions.addSearch;
/** @deprecated Use addSearch. Kept for compatibility. */
export const addSavedSearch = savedSearchesSlice.actions.addSearch;
/** @deprecated Use removeSearch. Kept for compatibility. */
export const removeRecentSearch = savedSearchesSlice.actions.removeSearch;
/** @deprecated Use removeSearch. Kept for compatibility. */
export const removeSavedSearch = savedSearchesSlice.actions.removeSearch;
/** @deprecated No longer used; single list only. */
export const setRecentSearches = setSavedSearches;

export default savedSearchesSlice.reducer;
